import { useEffect, useState } from 'react'
import { Alert, Snackbar } from '@mui/material'
import { subscribeToasts, type Toast } from '@/lib/toast'

/** How long a notice stays: an error is read more slowly than a «done». */
const DURATION_MS: Record<Toast['severity'], number> = { success: 4_000, info: 5_000, error: 8_000 }

/**
 * Draws `lib/toast.ts`'s notices, one at a time, in the corner: green for done,
 * red for what failed. A notice that arrives while another shows waits its turn
 * rather than replacing it — two quick actions each get their answer.
 */
export function Toaster() {
  const [queue, setQueue] = useState<Toast[]>([])
  const [open, setOpen] = useState(true)
  const current = queue[0]

  useEffect(() => subscribeToasts((t) => setQueue((q) => [...q, t])), [])

  return (
    <Snackbar
      key={current?.id}
      open={Boolean(current) && open}
      autoHideDuration={current ? DURATION_MS[current.severity] : null}
      onClose={(_, why) => {
        if (why !== 'clickaway') setOpen(false)
      }}
      TransitionProps={{
        onExited: () => {
          setQueue((q) => q.slice(1))
          setOpen(true)
        },
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      {current ? (
        <Alert
          severity={current.severity}
          variant="filled"
          onClose={() => setOpen(false)}
          sx={{ maxWidth: 480, boxShadow: 3 }}
        >
          {current.message}
        </Alert>
      ) : undefined}
    </Snackbar>
  )
}
